/**
 * AGC: Classic Potion Card Component
 * AGC: Displays classic potion information and purchase button
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect, useMemo } from 'react'
import { Heart, Sparkles, Zap, Skull, Info } from 'lucide-react'
import { formatEther, formatUnits } from 'viem'
import { useAccount } from 'wagmi'
import { buildBackendUrl } from '../utils/backendUrl'
import { PotionMintSuccessModal } from './modals/PotionMintSuccessModal'
import { useModal } from '../hooks/useModal'

// AGC: Classic Potion Types
// AGC: Potion image mapping: red=healing, blue=mana, purple=double strike, green=poison
// AGC: UPDATED 2025-11-27 - All potions scale with rarity: Common=1.00×, Uncommon=1.10×, Rare=1.20×, Epic=1.50×, Legendary=2.00×
export const CLASSIC_POTION_TYPES = [
  {
    id: 0,
    name: 'Healing Potion',
    emoji: '❤️',
    icon: Heart,
    image: '/potion_red.png',
    // AGC: INSTANT - Restores energy immediately when used
    description: 'Instantly restores +10 energy when used. One-time use per battle.',
    baseEffect: '+10 energy (instant)',
    detailedInfo: 'Use this potion to heal yourself during battle. Energy is restored immediately. Higher rarity = more healing (up to +15 at Legendary).',
    color: 'from-red-600 to-pink-600',
    borderColor: 'border-red-500'
  },
  {
    id: 1,
    name: 'Mana Potion',
    emoji: '✨',
    icon: Sparkles,
    image: '/potion_blue.png',
    // AGC: PERSISTENT - Once activated, boosts ALL buff activations for REST of battle
    description: 'Boosts buff power by +5% for ALL remaining turns once used.',
    baseEffect: '+5% buff power (persistent)',
    detailedInfo: 'Powerful persistent effect! Once used, EVERY buff that activates for the rest of the battle gets boosted. Shield blocks more, Pierce deals more damage. Effect lasts until battle ends.',
    color: 'from-purple-600 to-indigo-600',
    borderColor: 'border-purple-500'
  },
  {
    id: 2,
    name: 'Double Strike Potion',
    emoji: '⚡',
    icon: Zap,
    image: '/potion_purple.png',
    // AGC: CONDITIONAL - Only activates when you WIN that specific turn
    description: '+25% bonus damage, but ONLY when you WIN that turn.',
    baseEffect: '+25% damage (on win only)',
    detailedInfo: 'High risk, high reward! This potion only triggers when you WIN the rock-paper-scissors round. If you lose or draw, no bonus damage is applied. Time your attack wisely!',
    color: 'from-yellow-600 to-orange-600',
    borderColor: 'border-yellow-500'
  },
  {
    id: 3,
    name: 'Poison Potion',
    emoji: '☠️',
    icon: Skull,
    image: '/potion_green.png',
    // AGC: DOT (Damage Over Time) - 2.5 damage per turn for 5 turns = 12.5 total
    description: 'Poisons enemy for 2.5 damage per turn, lasting 5 turns.',
    baseEffect: '2.5 dmg × 5 turns = 12.5 total',
    detailedInfo: 'Damage over time effect! Once applied, your opponent takes 2.5 damage EVERY turn for 5 turns (12.5 total damage). The poison continues even if you lose rounds. Great for wearing down tanky opponents!',
    color: 'from-green-600 to-emerald-600',
    borderColor: 'border-green-500'
  }
] as const

interface ClassicPotionCardProps {
  potionType: typeof CLASSIC_POTION_TYPES[number]
  pricing?: {
    ogbPrice: string
    ethPrice: string
    usdcPrice: string
    hasDiscount: boolean
    baseUsdPrice: number
  }
  isLoadingPrice: boolean
  farmPro?: number
  onPurchase: (potionType: number, paymentMethod: 'usdc' | 'ogb' | 'eth') => Promise<void>
  ogbBalance: string
  getUSDCBalance: () => Promise<string>
}

/**
 * AGC: Classic Potion Card - Displays potion info and purchase button
 */
export const ClassicPotionCard: React.FC<ClassicPotionCardProps> = ({
  potionType,
  pricing,
  isLoadingPrice,
  farmPro,
  onPurchase,
  ogbBalance,
  getUSDCBalance
}) => {
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<'usdc' | 'ogb' | 'eth' | null>(null)
  
  // AGC: Modal hook for Base App compatible alerts
  const { alert, Modals } = useModal()
  const [usdcBalance, setUsdcBalance] = useState<string>('0')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [mintedPotionData, setMintedPotionData] = useState<{
    tokenId: number
    potionType: number
    rarity: number
    magnitude?: number
    txHash?: string
  } | null>(null)
  const { address } = useAccount()
  const Icon = potionType.icon

  // AGC: Load USDC balance
  useEffect(() => {
    if (address && getUSDCBalance) {
      getUSDCBalance().then(setUsdcBalance).catch(() => setUsdcBalance('0'))
    }
  }, [address, getUSDCBalance])

  const handlePurchase = async () => {
    if (!paymentMethod || isPurchasing) return
    
    try {
      setIsPurchasing(true)
      const result = await onPurchase(potionType.id, paymentMethod)
      
      // AGC: Check if result contains potionData (new format)
      if (result && typeof result === 'object' && 'potionData' in result && result.potionData) {
        setMintedPotionData({
          tokenId: result.potionData.tokenId,
          potionType: result.potionData.potionType,
          rarity: result.potionData.rarity,
          magnitude: result.potionData.magnitude,
          txHash: result.potionData.txHash || result.receipt?.hash
        })
        setShowSuccessModal(true)
      } else if (result && typeof result === 'object' && 'receipt' in result) {
        // AGC: Fallback - try to get tokenId from receipt logs
        const receipt = result.receipt
        if (receipt && receipt.logs) {
          // AGC: Try to extract tokenId from Transfer event
          // This is a fallback if PotionPurchased event parsing fails
          setMintedPotionData({
            tokenId: 0, // AGC: Will be updated when we can parse it
            potionType: potionType.id,
            rarity: 0, // AGC: Unknown rarity
            txHash: receipt.hash
          })
          setShowSuccessModal(true)
        }
      }
    } catch (error: any) {
      console.error('Purchase failed:', error)
      await alert(`Purchase failed: ${error.message || 'Unknown error'}`, { type: 'error' })
    } finally {
      setIsPurchasing(false)
      setPaymentMethod(null)
    }
  }

  const priceContent = useMemo(() => {
    if (isLoadingPrice || !pricing) {
      return <span className="text-xs text-white/70">Loading price...</span>
    }

    // AGC: Convert string prices to numbers (same pattern as BuffCard)
    const usdcValue = typeof pricing.usdcPrice === 'string' ? parseFloat(pricing.usdcPrice) : (pricing.usdcPrice || 0)
    const ethValue = typeof pricing.ethPrice === 'string' ? parseFloat(pricing.ethPrice) : (pricing.ethPrice || 0)
    const ogbValue = typeof pricing.ogbPrice === 'string' ? parseFloat(pricing.ogbPrice) : (pricing.ogbPrice || 0)

    const hasDiscount = pricing.hasDiscount
    // AGC: FIX (2025-11-27) - Contract already returns discounted prices, don't double-discount!
    // AGC: If hasDiscount is true, the pricing values are ALREADY the discounted amount
    // AGC: We only show the "strikethrough" full price for display using baseUsdPrice

    // AGC: Calculate full prices for display (when showing strikethrough)
    // AGC: baseUsdPrice is the full $0.02, discounted is $0.01
    const fullPriceMultiplier = hasDiscount ? 2.0 : 1.0 // AGC: Reverse the discount to show full price

    return (
      <div className="space-y-1 text-xs text-white/80">
        {hasDiscount && (
          <div className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold mb-2 text-center">
            🎉 50% OFF (Staked)
          </div>
        )}
        <div className="flex justify-between">
          <span>USDC</span>
          <span>
            {hasDiscount && <span className="line-through text-gray-500 mr-1">{(parseFloat(pricing.usdcPrice) * fullPriceMultiplier).toFixed(4)}</span>}
            {parseFloat(pricing.usdcPrice).toFixed(4)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>ETH</span>
          <span>
            {hasDiscount && <span className="line-through text-gray-500 mr-1">{(parseFloat(pricing.ethPrice) * fullPriceMultiplier).toFixed(6)}</span>}
            {parseFloat(pricing.ethPrice).toFixed(6)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>OGB</span>
          <span>
            {hasDiscount && <span className="line-through text-gray-500 mr-1">{(parseFloat(pricing.ogbPrice) * fullPriceMultiplier).toFixed(4)}</span>}
            {parseFloat(pricing.ogbPrice).toFixed(4)}
          </span>
        </div>
      </div>
    )
  }, [isLoadingPrice, pricing])

  return (
    <div className="relative flex flex-col h-full bg-gradient-to-br from-slate-900/90 via-slate-800/90 to-slate-900/90 rounded-xl border border-slate-700/50 p-4 hover:border-slate-600 transition-all">
      {/* AGC: Potion Icon */}
      <div className="flex items-center justify-center mb-3">
        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${potionType.color} flex items-center justify-center shadow-lg p-2`}>
          <img 
            src={potionType.image || '/potion_red.png'} 
            alt={potionType.name}
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* AGC: Potion Name */}
      <h3 className="text-base font-bold text-white mb-1 text-center">{potionType.name}</h3>

      {/* AGC: Base Effect with Info Icon */}
      <div className="flex items-center justify-center gap-1.5 mb-3">
        <p className="text-xs text-slate-400 text-center">{potionType.baseEffect}</p>
        <button
          onClick={() => {
            // AGC: Show rarity values for this potion type
            let rarityInfo = ''
            if (potionType.id === 0) { // Healing
              rarityInfo = 'Healing Potion Rarity Effects:\n• Common: +10 energy\n• Uncommon: +11 energy\n• Rare: +12 energy\n• Epic: +15 energy\n• Legendary: +20 energy'
            } else if (potionType.id === 1) { // Mana
              rarityInfo = 'Mana Potion Rarity Effects:\n• Common: +5% buff power\n• Uncommon: +5.5% buff power\n• Rare: +6% buff power\n• Epic: +7.5% buff power\n• Legendary: +10% buff power'
            } else if (potionType.id === 2) { // Double Strike
              rarityInfo = 'Double Strike Potion Rarity Effects:\n• Common: +25% damage (on win)\n• Uncommon: +27.5% damage (on win)\n• Rare: +30% damage (on win)\n• Epic: +37.5% damage (on win)\n• Legendary: +50% damage (on win)'
            } else if (potionType.id === 3) { // Poison
              rarityInfo = 'Poison Potion Rarity Effects:\n• Common: 2.5 dmg/turn × 5 turns\n• Uncommon: 2.75 dmg/turn × 5 turns\n• Rare: 3.0 dmg/turn × 5 turns\n• Epic: 3.75 dmg/turn × 5 turns\n• Legendary: 5.0 dmg/turn × 5 turns'
            }
            alert(rarityInfo, { type: 'info', title: 'Rarity Effects' })
          }}
          className="p-0.5 rounded-full bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-slate-300 transition-colors"
          title="View rarity effects"
        >
          <Info className="w-3 h-3" />
        </button>
      </div>

      {/* AGC: FarmPro Score Line */}
      {farmPro !== undefined && (
        <div className="mb-3 text-center">
          <div className="text-[10px] text-slate-400">
            {Math.round(farmPro * 100)}% improve chances by FarmPro Score
          </div>
        </div>
      )}

      {/* AGC: Pricing */}
      <div className="mb-4 flex-1">
        {priceContent}
      </div>

      {/* AGC: Purchase Button */}
      {!paymentMethod ? (
        <div className="space-y-2">
          <button
            onClick={() => setPaymentMethod('ogb')}
            disabled={isPurchasing || parseFloat(ogbBalance) < parseFloat(pricing?.ogbPrice || '0')}
            className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold hover:from-blue-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Mint with OGB
          </button>
          <button
            onClick={() => setPaymentMethod('usdc')}
            disabled={isPurchasing || parseFloat(usdcBalance) < parseFloat(pricing?.usdcPrice || '0')}
            className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-semibold hover:from-green-500 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Mint with USDC
          </button>
          <button
            onClick={() => setPaymentMethod('eth')}
            disabled={isPurchasing}
            className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Mint with ETH
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={handlePurchase}
            disabled={isPurchasing}
            className="w-full py-2 px-3 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-semibold hover:from-green-500 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isPurchasing ? 'Minting...' : `Confirm ${paymentMethod.toUpperCase()} Mint`}
          </button>
          <button
            onClick={() => setPaymentMethod(null)}
            disabled={isPurchasing}
            className="w-full py-1 px-3 rounded-lg bg-slate-700 text-white text-xs hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Cancel
          </button>
        </div>
      )}

      {/* AGC: Success Modal */}
      <PotionMintSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false)
          setMintedPotionData(null)
        }}
        potionData={mintedPotionData}
      />

      {/* AGC: Modal components */}
      {Modals}
    </div>
  )
}

